<template>
  <div class="expenses-view">
    <div class="expenses-view__grids">
      <ExpensesGrid
        :columns="summaryExpensesColumns"
        :dataItems="summaryExpensesItems"
        footerText="Total expenses reimburseable to RFR"
        :footerValue="CurrencyFormatter.format(totalSummaryExpenses)"
        :loading="loading"
      />
      <ExpensesGrid
        :columns="ouExpensesColumns"
        :dataItems="ouExpensesItems"
        footerText="Total OU expenses"
        :footerValue="CurrencyFormatter.format(totalOuExpenses)"
        :loading="loading"
      />
    </div>
    <span class="expenses-view__total pull-right">
      Total expenses (OU + RFR):
      {{ CurrencyFormatter.format(totalSummaryExpenses + totalOuExpenses) }}
    </span>
  </div>
</template>

<script setup lang="ts">
import { api } from "@/api";
import { handleResponsePromiseAsync } from "@/shared/helpers/apiHelper";
import { computed, onBeforeMount, ref, watch } from "vue";
import ExpensesGrid from "./ExpensesGrid.vue";
import type { TripExpensesSummaryDisplay } from "@/Domain/TripExpensesSummaryDisplay";
import { CurrencyFormatter } from "@/shared/helpers/formatHelper";
import { useExportStore } from "@/stores/useExportStore";
import {
  type WorkbookOptions,
  workbookOptions,
} from "@progress/kendo-vue-excel-export";
import { copyRowsToSheet } from "@/shared/helpers/exportHelper";

const exportStore = useExportStore();

const props = defineProps<{
  tripId: number;
}>();

const tripExpenses = ref<TripExpensesSummaryDisplay>();
const loading = ref(false);

const defaultColumns = [
  {
    title: "",
    children: [
      {
        title: "Expense",
        field: "text",
        headerCell: "emptyHeader",
        footer: () => {
          return "Total";
        },
      },
      {
        title: "Value",
        field: "value",
        headerCell: "emptyHeader",
        className: "text-right",
        format: "{0:c}",
      },
    ],
    footerCell: "footer",
  },
];

const summaryExpensesColumns = computed(() => {
  const columns = [{ ...defaultColumns[0], title: "Summary of Expenses" }];
  columns[0].children = columns[0].children.map((child) => {
    return { ...child };
  });
  columns[0].children[1].footer = () => {
    return totalSummaryExpenses.value.toString();
  };
  return columns;
});

const ouExpensesColumns = computed(() => {
  const columns = [{ ...defaultColumns[0], title: "OU Expenses" }];
  columns[0].children = columns[0].children.map((child) => {
    return { ...child };
  });
  columns[0].children[1].footer = () => {
    return totalOuExpenses.value.toString();
  };
  return columns;
});

const summaryExpensesItems = computed(() => {
  if (!tripExpenses.value) return [];

  return [
    {
      text: `Mileage Reimbursed for ${tripExpenses.value.Miles} miles`,
      value: tripExpenses.value.MileageReimbursed,
    },
    { text: "Airfare", value: tripExpenses.value.Airfare },
    { text: "Car Rental", value: tripExpenses.value.CarRental },
    { text: "Tolls", value: tripExpenses.value.Tolls },
    { text: "Parking", value: tripExpenses.value.Parking },
    { text: "Gas", value: tripExpenses.value.Gas },
    { text: "Taxi", value: tripExpenses.value.Taxi },
    { text: "Motel", value: tripExpenses.value.Motel },
    { text: "Food", value: tripExpenses.value.Food },
    { text: "Misc", value: tripExpenses.value.Misc },
  ];
});

const ouExpensesItems = computed(() => {
  if (!tripExpenses.value) return [];

  return [
    {
      text: "OU Air",
      value: tripExpenses.value.OuAir,
    },
    { text: "OU Car Rental", value: tripExpenses.value.OuCarRental },
    { text: "OU Mileage", value: tripExpenses.value.OuMiles },
    { text: "OU Misc expenses", value: tripExpenses.value.OuOther },
  ];
});

const totalSummaryExpenses = computed(() => {
  return summaryExpensesItems.value.reduce((acc, item) => acc + item.value, 0);
});

const totalOuExpenses = computed(() => {
  return ouExpensesItems.value.reduce((acc, item) => acc + item.value, 0);
});

const getTripExpensesAsync = async function () {
  loading.value = true;

  await handleResponsePromiseAsync(
    api.get("GetTripExpensesSummary", {
      tripId: props.tripId,
      newTrip: false,
      caller: "Accounting",
    }),
    (data) => {
      tripExpenses.value = data;
    },
    "SummaryExpensesGrid.vue",
    "getTripExpensesAsync"
  );

  loading.value = false;
};

const onExport = function (options: WorkbookOptions | null) {
  if (!options || !options.sheets) return;

  let optionsTemp = workbookOptions({
    data: summaryExpensesItems.value,
    columns: summaryExpensesColumns.value,
  });
  const headerIndex = options.sheets![0].rows!.length + 1;
  copyRowsToSheet(options, optionsTemp);

  optionsTemp = workbookOptions({
    data: ouExpensesItems.value,
    columns: ouExpensesColumns.value,
  });
  options.sheets[0].rows?.push({});
  let index = headerIndex;
  optionsTemp.sheets![0].rows?.forEach((row) => {
    options.sheets![0].rows![index].cells?.push({});
    row.cells?.forEach((cell) => {
      options.sheets![0].rows![index].cells?.push(cell);
    });
    index++;
  });

  options.sheets[0].rows?.push({});
  options.sheets[0].rows?.push({
    cells: [
      {
        value: "Total expenses (OU + RFR)",
        background: "#007bff",
        color: "#fff",
        colSpan: 1,
      },
      {
        value: totalSummaryExpenses.value + totalOuExpenses.value,
        background: "#007bff",
        color: "#fff",
        colSpan: 1,
      },
    ],
  });
};

exportStore.subscribe(onExport);

watch(
  () => props.tripId,
  () => {
    getTripExpensesAsync();
  }
);

onBeforeMount(() => {
  getTripExpensesAsync();
});
</script>

<style lang="scss">
.expenses-view {
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: flex-end;

  &__grids {
    display: flex;
    flex-direction: row;
    gap: 20px;
    align-items: flex-start;
  }

  &__total {
    display: block;
    font-weight: bold;
    width: 50%;
    font-size: 16px;
    color: #007bff;
  }

  .text-right {
    text-align: right;
  }
}
</style>
